import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  TemplateRef,
  Directive,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import type { ColumnDef, TableAction } from './data-table.types';

/**
 * Directiva para registrar templates custom de celdas.
 * Uso en el padre:
 * ```html
 * <ng-template appCellTemplate="acciones" let-row let-col="column">
 *   <button (click)="ver(row)">Ver</button>
 * </ng-template>
 * ```
 */
@Directive({
  selector: '[appCellTemplate]',
  standalone: true,
})
export class CellTemplateDirective {
  @Input('appCellTemplate') name = '';
  constructor(public templateRef: TemplateRef<any>) {}
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzSpinModule,
    NzTagModule,
    NzInputModule,
    NzIconModule,
    NzEmptyModule,
    NzTooltipModule,
  ],
  templateUrl: './data-table.html',
  styleUrl: './data-table.css',
})
export class DataTable<T = any> {
  @Input() data: T[] = [];
  @Input() columns: ColumnDef<T>[] = [];
  @Input() loading = false;
  @Input() pageSize = 10;
  @Input() pageSizeOptions = [10, 20, 50, 100];
  @Input() searchable = true;
  @Input() searchPlaceholder = 'Buscar...';
  @Input() emptyText = 'No se encontro informacion';
  @Input() tableSize: 'small' | 'middle' | 'default' = 'small';
  @Input() showAddButton = false;
  @Input() disableAddButton = false;
  @Input() addButtonTooltip = 'Agregar';
  @Input() showRefreshButton = false;

  @Output() rowClick = new EventEmitter<T>();
  @Output() action = new EventEmitter<TableAction<T>>();
  @Output() add = new EventEmitter<void>();
  @Output() refresh = new EventEmitter<void>();

  @ContentChildren(CellTemplateDirective) cellTemplates!: QueryList<CellTemplateDirective>;

  searchText = '';
  pageIndex = 1;
  sortKey: string | null = null;
  sortOrder: 'ascend' | 'descend' | null = null;

  get filteredData(): T[] {
    let result = this.data;

    // Filtro por texto
    if (this.searchText.trim()) {
      const term = this.searchText.toLowerCase();
      const filterableKeys = this.columns
        .filter(c => c.filterable)
        .map(c => c.key);

      result = result.filter(row =>
        filterableKeys.some(key => {
          const value = this.getNestedValue(row, key);
          return value != null && String(value).toLowerCase().includes(term);
        }),
      );
    }

    // Sorting
    if (this.sortKey && this.sortOrder) {
      const key = this.sortKey;
      const dir = this.sortOrder === 'ascend' ? 1 : -1;
      result = [...result].sort((a, b) => {
        const va = this.getNestedValue(a, key);
        const vb = this.getNestedValue(b, key);
        if (va == null && vb == null) return 0;
        if (va == null) return -dir;
        if (vb == null) return dir;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb)) * dir;
      });
    }

    return result;
  }

  get paginatedData(): T[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    return this.filteredData.slice(start, start + this.pageSize);
  }

  get totalFiltered(): number {
    return this.filteredData.length;
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((o, k) => o?.[k], obj);
  }

  getCellTemplate(key: string): TemplateRef<any> | null {
    const tpl = this.cellTemplates?.find(t => t.name === key);
    return tpl?.templateRef ?? null;
  }

  onSort(col: ColumnDef<T>): void {
    if (!col.sortable) return;

    if (this.sortKey === col.key) {
      // Cycle: ascend → descend → null
      if (this.sortOrder === 'ascend') {
        this.sortOrder = 'descend';
      } else {
        this.sortKey = null;
        this.sortOrder = null;
      }
    } else {
      this.sortKey = col.key;
      this.sortOrder = 'ascend';
    }
  }

  onSearch(): void {
    this.pageIndex = 1;
  }

  clearSearch(): void {
    this.searchText = '';
    this.pageIndex = 1;
  }

  onPageIndexChange(index: number): void {
    this.pageIndex = index;
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
  }

  onRowClick(row: T): void {
    this.rowClick.emit(row);
  }

  emitAction(actionName: string, row: T, event: Event): void {
    event.stopPropagation();
    this.action.emit({ action: actionName, row });
  }
}
